import { isEqual } from "lodash";
import React, {
  createContext,
  useContext,
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useReducer,
  useRef,
} from "react";
import { EditorDefns } from "../utility";
import { computeHeaderSpanState, HeaderSpanMap } from "./computeHeaderSpanState";
import {
  CLICK_OUTSIDE,
  COLS_CHANGED,
  IScaffoldAction,
  ROWS_CHANGED,
  ScaffoldState,
  SELECTION_CHANGED,
  SET_DRAGGING,
} from "./context/interface";
import { scaffoldReducer } from "./context/scaffoldReducer";
import {
  ColScaffold,
  DEFAULT_SCAFFOLD_ZONE_COLORS,
  EMPTY_SCAFFOLD_SELECTION,
  RowScaffold,
  ScaffoldDropZone,
  ScaffoldHeaderCellSpans,
  ScaffoldSelection,
  ScaffoldZoneColors,
} from "./interface";
import { reconcileScaffoldItems } from "./reconcileScaffoldItems";
import styles from "./Scaffold.module.css";
import { ScaffoldColResizeHandle } from "./ScaffoldColResizeHandle";
import { ScaffoldColSelectHandle } from "./ScaffoldColSelectHandle";
import { ScaffoldRowGroups } from "./ScaffoldRowGroups";

/**
 * Type definitions for the Scaffold component and its context, including props for the scaffold, columns, rows, and cells.
 */
export interface ScaffoldProps<T> {
  /** Component id */
  id: string;
  /** Default cell component for the scaffold */
  cellComponent: EditorDefns<T>;
  /** Nested record for rowId -> colId -> cell value */
  cellData: Record<string, Record<string, T>>;
  /** Column definitions */
  columns: ColScaffold<T>[];
  /** Flag to disable interaction with the scaffold */
  disabled?: boolean;
  /**
   * Optional per-cell span definitions that allow header (and other) cells to span multiple
   * consecutive columns and/or rows.  Each entry maps a row ID → column ID → span.
   * Cells not listed here are rendered normally (1×1).
   */
  headerCellSpans?: ScaffoldHeaderCellSpans;
  /**
   * Horizontal justification of the scaffold table within its container.
   * Affects how column-resize drags track the mouse: "left" uses a 1:1 right-edge delta,
   * "center" doubles the delta so the right edge tracks the pointer as the table expands
   * symmetrically, and "right" uses the left edge of the column and inverts the delta.
   * Defaults to "left".
   */
  justification?: "left" | "center" | "right";
  /** Callback for when a cell changes */
  onChangeCell?: (rowId: string, colId: string, value: T) => void;
  /** Callback for when a column changes */
  onChangeCol?: (colId: string, value: ColScaffold<T>) => void;
  /** Callback for when a row changes */
  onChangeRow?: (rowId: string, value: RowScaffold<T>) => void;
  /** Callback for when an item is dropped onto a cell, including the classified drop zone */
  onDrop?: (
    event: React.DragEvent<HTMLDivElement>,
    rowId: string,
    colId: string,
    zone: ScaffoldDropZone,
  ) => void;
  /** Callback invoked when the selection changes, e.g. due to ctrl+click or clicking outside the table. */
  onSelectionChange?: (selection: ScaffoldSelection) => void;
  /** Row definitions */
  rows: RowScaffold<T>[];
  /** Current selection state, controlling which cells, rows, and columns are highlighted. */
  selection?: ScaffoldSelection;
  /** Container style for the scaffold component */
  style?: React.CSSProperties;
  /** Zone highlight configuration for the drop zone overlay. Defaults to DEFAULT_SCAFFOLD_ZONE_COLORS. */
  zoneColors?: ScaffoldZoneColors;
}

/**
 * Static configuration for the scaffold component, covering props that are passed from the parent
 * and are not changed by user interaction within the scaffold.
 */
export interface ScaffoldSettings<T> {
  /** Unique identifier for the scaffold element in the DOM. */
  id: string;
  /** Default cell component used to render scaffold cells. */
  cellComponent: EditorDefns<T>;
  /** Nested record mapping rowId → colId → cell value. */
  cellData: Record<string, Record<string, T>>;
  /** Column definitions provided by the parent. */
  columns: ColScaffold<T>[];
  /** Row definitions provided by the parent. */
  rows: RowScaffold<T>[];
  /** When true, interaction with all scaffold cells is disabled. */
  disabled?: boolean;
  /**
   * Horizontal justification of the scaffold table within its container.
   * Affects how column-resize drags track the mouse: "left" uses a 1:1 right-edge delta,
   * "center" doubles the delta so the right edge tracks the pointer as the table expands
   * symmetrically, and "right" uses the left edge of the column and inverts the delta.
   * Defaults to "left".
   */
  justification?: "left" | "center" | "right";
  /** Zone highlight configuration for the drop zone overlay. */
  zoneColors: ScaffoldZoneColors;
  /** Callback invoked when a column definition changes (e.g., after a resize). */
  onChangeCol?: (colId: string, value: ColScaffold<T>) => void;
  /** Callback invoked when a row definition changes. */
  onChangeRow?: (rowId: string, value: RowScaffold<T>) => void;
  /** Callback invoked when a cell value changes. */
  onChangeCell?: (rowId: string, colId: string, value: T) => void;
  /** Callback invoked when an item is dropped onto a cell. */
  onDrop?: (
    event: React.DragEvent<HTMLDivElement>,
    rowId: string,
    colId: string,
    zone: ScaffoldDropZone,
  ) => void;
  /** Callback invoked when the selection changes, e.g. due to user clicks or clicking outside. */
  onSelectionChange?: (selection: ScaffoldSelection) => void;
  /**
   * Optional per-cell span definitions.  Passed through from the component props so that child
   * cells can use them without needing the raw props directly.
   */
  headerCellSpans?: ScaffoldHeaderCellSpans;
}

/**
 * Context value for the scaffold, exposing the reducer dispatch, full internal state,
 * static settings derived from the parent's props, and the pre-computed header span map.
 */
export interface ScaffoldContextProps<T> {
  /** Dispatcher for all scaffold reducer actions. */
  dispatch: React.Dispatch<IScaffoldAction<T>>;
  /** Current internal state managed by the scaffold reducer. */
  state: ScaffoldState<T>;
  /** Static configuration and callbacks passed from the parent component. */
  settings: ScaffoldSettings<T>;
  /**
   * Pre-computed rendering information for every cell affected by `settings.headerCellSpans`.
   * Cells not present in this map should be rendered normally.
   * Derived from settings and state on every render; consumers should treat it as read-only.
   */
  headerSpanMap?: HeaderSpanMap;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ScaffoldContext = createContext<ScaffoldContextProps<any> | null>(null);

/**
 * Scaffold component for rendering a table with custom cell components and layout.
 *
 * @param props - The props for the Scaffold component.
 * @param props.id - Unique identifier for the scaffold.
 * @param props.cellComponent - The default cell component to use for all cells in the scaffold.
 * @param props.cellData - A nested record mapping row IDs to column IDs to cell values.
 * @param props.columns - An array of column definitions, each specifying an optional custom cell component and width.
 * @param props.disabled - Optional flag to disable interaction with the scaffold.
 * @param props.headerCellSpans - Optional per-cell span definitions allowing cells to span multiple columns and/or rows.
 * @param props.justification - Optional horizontal justification of the scaffold table within its container. Affects how column-resize drags track the mouse. Defaults to "left".
 * @param props.onChangeCell - Optional callback invoked when a cell value changes.
 * @param props.onChangeCol - Optional callback invoked when a column definition changes (e.g., after a resize).
 * @param props.onChangeRow - Optional callback invoked when a row definition changes.
 * @param props.onDrop - Optional callback invoked when an item is dropped onto a cell, receiving the drag event, row id, column id, and classified drop zone.
 * @param props.onSelectionChange - Optional callback invoked when the selection changes via user interaction.
 * @param props.rows - An array of row definitions, each specifying an optional custom cell component, height, and text alignment.
 * @param props.selection - Optional selection state controlling which cells, rows, and columns are highlighted.
 * @param props.style - Optional container style for the scaffold component.
 * @param props.zoneColors - Optional per-zone highlight colours for the drop zone overlay.
 * @returns A React element representing the scaffold.
 */
export const Scaffold = <T,>(props: ScaffoldProps<T>) => {
  const [state, dispatch] = useReducer(
    (s: ScaffoldState<T>, a: Parameters<typeof scaffoldReducer<T>>[1]) => scaffoldReducer(s, a),
    undefined,
    (): ScaffoldState<T> => ({
      prevCols: props.columns,
      prevRows: props.rows,
      enteringColIds: new Set<string>(),
      enteringRowIds: new Set<string>(),
      leavingCols: [],
      leavingRows: [],
      isDragging: false,
      previewColWidths: {},
      previewRowHeights: {},
      selection: props.selection ?? EMPTY_SCAFFOLD_SELECTION,
      selectionAnchor: null,
    }),
  );

  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const prevSelectionPropRef = useRef(props.selection);

  const handleDragStart = useEffectEvent(() =>
    dispatch({ operation: SET_DRAGGING, isDragging: true }),
  );
  const handleDragEnd = useEffectEvent(() =>
    dispatch({ operation: SET_DRAGGING, isDragging: false }),
  );

  const handleClickOutside = useEffectEvent((event: MouseEvent) => {
    if (
      tableWrapperRef.current &&
      !tableWrapperRef.current.contains(event.target as Node) &&
      props.onSelectionChange
    ) {
      dispatch({ operation: CLICK_OUTSIDE });
    }
  });

  useEffect(() => {
    document.addEventListener("dragstart", handleDragStart);
    document.addEventListener("dragend", handleDragEnd);
    return () => {
      document.removeEventListener("dragstart", handleDragStart);
      document.removeEventListener("dragend", handleDragEnd);
    };
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // These are outside a useEffect for animation purposes
  if (reconcileScaffoldItems(props.columns, state.prevCols, state.leavingCols).hasChanged) {
    dispatch({ operation: COLS_CHANGED, cols: props.columns });
  }
  if (reconcileScaffoldItems(props.rows, state.prevRows, state.leavingRows).hasChanged) {
    dispatch({ operation: ROWS_CHANGED, rows: props.rows });
  }

  const onSelectionChangeRef = useRef(props.onSelectionChange);

  useLayoutEffect(() => {
    onSelectionChangeRef.current = props.onSelectionChange;
  });
  useLayoutEffect(() => {
    if (props.selection !== undefined && !isEqual(props.selection, prevSelectionPropRef.current)) {
      prevSelectionPropRef.current = props.selection;
      dispatch({ operation: SELECTION_CHANGED, selection: props.selection });
    }
  }, [props.selection]);
  useLayoutEffect(() => {
    if (!isEqual(state.selection, props.selection ?? EMPTY_SCAFFOLD_SELECTION)) {
      onSelectionChangeRef.current?.(state.selection);
      prevSelectionPropRef.current = state.selection;
    }
  }, [state.selection, props.selection]);

  /**
   * Prevents browser text selection on shift+mousedown when cell selection is already active.
   * This keeps shift+click range selection focused on scaffold behaviour instead of selecting text.
   *
   * @param event - Mouse event raised while pressing on the scaffold.
   */
  const onTableMouseDownCapture = (event: React.MouseEvent<HTMLDivElement>) => {
    const hasActiveCellSelection = state.selection.selectedCells.length > 0;
    if (event.shiftKey && hasActiveCellSelection) {
      event.preventDefault();
    }
  };

  const scaffoldContextValue: ScaffoldContextProps<T> = {
    dispatch,
    state,
    settings: {
      id: props.id,
      cellComponent: props.cellComponent,
      cellData: props.cellData,
      columns: props.columns,
      rows: props.rows,
      disabled: props.disabled,
      justification: props.justification,
      zoneColors: props.zoneColors ?? DEFAULT_SCAFFOLD_ZONE_COLORS,
      onChangeCol: props.onChangeCol,
      onChangeRow: props.onChangeRow,
      onChangeCell: props.onChangeCell,
      onDrop: props.onDrop,
      onSelectionChange: props.onSelectionChange,
      headerCellSpans: props.headerCellSpans,
    },
    headerSpanMap:
      props.headerCellSpans !== undefined
        ? computeHeaderSpanState(
            props.rows,
            props.columns,
            props.headerCellSpans,
            state.previewColWidths,
            state.previewRowHeights,
          )
        : undefined,
  };

  const orderedCols = [...props.columns].sort((a, b) => a.colNumber - b.colNumber);
  const shouldRenderSelectionHandles = props.onSelectionChange !== undefined;

  let nextSelectLeft = 0;
  const colSelectLayout = orderedCols.map((col) => {
    const colLeft = nextSelectLeft;
    const colWidth = state.previewColWidths[col.id] ?? col.width;
    nextSelectLeft += colWidth;
    return { colId: col.id, left: colLeft, width: colWidth };
  });

  return (
    <ScaffoldContext.Provider value={scaffoldContextValue}>
      <div
        className={styles.tableWrapper}
        onMouseDownCapture={onTableMouseDownCapture}
        ref={tableWrapperRef}
        style={props.style}
      >
        <div
          className={styles.table}
          id={props.id}
        >
          <ScaffoldRowGroups />
          <div className={styles.colResizeOverlay}>
            {shouldRenderSelectionHandles &&
              colSelectLayout.map((colSelect) => (
                <ScaffoldColSelectHandle
                  key={`col-select-handle-${colSelect.colId}`}
                  colId={colSelect.colId}
                  left={colSelect.left}
                  width={colSelect.width}
                />
              ))}
            {orderedCols
              .filter((oc) => !oc.noResize)
              .map((oc, ix, arr) => {
                let id = oc.id;
                // istanbul ignore next - added for debugging bad data
                if (arr.findIndex((col) => col.id === oc.id) !== ix) {
                  console.warn(
                    "Having to reassign colId for resize handle due to non-unique column IDs:",
                    oc.id,
                  );
                  id = `${oc.id}-${ix}`;
                }
                return (
                  <ScaffoldColResizeHandle
                    key={`col-resize-handle-${id}`}
                    colId={oc.id}
                  />
                );
              })}
          </div>
        </div>
      </div>
    </ScaffoldContext.Provider>
  );
};

Scaffold.displayName = "Scaffold";

/**
 * Custom hook to access the Scaffold context.
 *
 * @returns The current Scaffold context value.
 * @throws Will throw an error if used outside of a ScaffoldContext provider.
 */
export const useScaffold = <T,>(): ScaffoldContextProps<T> => {
  const context = useContext(ScaffoldContext);
  if (!context) {
    throw new Error("useScaffold must be used within a ScaffoldContext provider");
  }
  return context;
};
